export const YOUTUBE_URL = "https://www.youtube.com/@theoperatingreality";
export const YOUTUBE_HANDLE = "@theoperatingreality";
export const VIDEO_MANAGER_BOTTLENECK = "UYGJb5S0_Pk";
export const LINKEDIN_URL = "https://www.linkedin.com/in/anna-krat-34a17328/";
export const EXPERIENCE_URL = "https://annakrat.co.uk/";

export const NAV_LINKS = [
  { label: "How Work Actually Works", to: "/ideas", testId: "nav-link-ideas" },
  { label: "Learn", to: "/learn", testId: "nav-link-learn" },
  { label: "Consulting", to: "/consulting", testId: "nav-link-consulting" },
  { label: "About", to: "/about", testId: "nav-link-about" },
];

export const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "people", label: "People" },
  { id: "process", label: "Process" },
  { id: "technology", label: "Technology" },
  { id: "governance", label: "Governance" },
  { id: "ai", label: "AI & Automation" },
];

export const MARQUEE_ITEMS = [
  "Processes over chaos",
  "Insights over noise",
  "Systems over symptoms",
  "Outcomes over activity",
  "See how work actually works",
];

export const EXPLORE_AREAS = [
  {
    id: "process",
    index: "01",
    title: "PROCESS",
    glyph: "process",
    text: "Where work gets stuck, duplicated or unnecessarily complicated.",
  },
  {
    id: "people",
    index: "02",
    title: "PEOPLE",
    glyph: "people",
    text: "Where organisations create dependencies around individuals rather than systems.",
  },
  {
    id: "technology",
    index: "03",
    title: "TECHNOLOGY",
    glyph: "technology",
    text: "When technology genuinely solves a problem — and when it only hides one.",
  },
  {
    id: "governance",
    index: "04",
    title: "GOVERNANCE",
    glyph: "governance",
    text: "How ownership, policies, controls and accountability work in reality.",
  },
  {
    id: "ai",
    index: "05",
    title: "AI & AUTOMATION",
    glyph: "ai",
    text: "What should be automated, what shouldn't, and what needs to be fixed first.",
  },
];

export const MANIFESTO = [
  { n: "01", text: "A slow process isn't always a technology problem." },
  { n: "02", text: "A bottleneck isn't always a people problem." },
  { n: "03", text: "And automation doesn't fix a broken operating model." },
];

export const IDEAS = [
  {
    slug: "manager-bottleneck",
    cats: ["people"],
    tag: "PEOPLE · OPERATING MODEL",
    title: "The Manager Who Became the Bottleneck",
    insight:
      "The problem isn't necessarily that the manager is too busy. The organisation may have designed a system that requires them to be involved in everything.",
    format: "EXPLORE",
    href: "/patterns/manager-bottleneck",
    featured: true,
    symptom: "Everything waits for one person.",
    underlying:
      "Decision-making and organisational knowledge have become concentrated around one manager.",
    lens: ["Workload", "Dependency", "Ownership", "Decision Rights", "Delegation"],
    pattern: {
      symptom: "Every decision, exception and question routes to the same manager.",
      finding: "Critical knowledge and decision rights exist only in that one person.",
      rootCause: "The operating model made the manager part of every decision mechanism.",
      recommendation: "Define decision rights, capture the knowledge, and design escalation around exceptions.",
    },
  },
  {
    slug: "spreadsheet-company",
    cats: ["process"],
    tag: "PROCESS · INFORMATION",
    title: "The Spreadsheet That Runs the Company",
    insight: "Don't replace the spreadsheet before understanding why it became necessary.",
    format: "EXPLORE",
    href: "/patterns/spreadsheet-company",
    symptom: "Everyone relies on the same spreadsheet to keep the business moving.",
    underlying:
      "The spreadsheet has become a workaround for missing process, ownership or system capability.",
    lens: ["Spreadsheet", "Information Flow", "Ownership", "Process", "System"],
  },
  {
    slug: "policy-nobody-reads",
    cats: ["governance"],
    tag: "GOVERNANCE",
    title: "The Policy Nobody Reads",
    insight:
      "A policy isn't effective because it exists. It is effective when it changes behaviour and creates evidence.",
    format: "EXPLORE",
    href: "/patterns/policy-nobody-reads",
    symptom: "The organisation has a policy, but nobody follows it.",
    underlying: "The policy may be disconnected from the way people actually work.",
    lens: ["Policy", "Awareness", "Ownership", "Process", "Evidence"],
  },
  {
    slug: "employee-no-laptop",
    cats: ["people", "process", "technology"],
    tag: "PEOPLE · PROCESS · IT",
    title: "The Employee Who Couldn't Get a Laptop",
    insight:
      "The problem isn't necessarily IT. It may be the absence of a standardised decision mechanism.",
    format: "EXPLORE",
    href: "/patterns/employee-no-laptop",
    symptom: "A new employee arrives without the equipment they need.",
    underlying:
      "The onboarding process depends on a manager supplying information that only they know.",
    lens: ["Request", "Information", "Decision", "Ownership", "Fulfilment"],
  },
  {
    slug: "meeting-should-be-email",
    cats: ["process"],
    tag: "PROCESS",
    title: "The Meeting That Should Have Been an Email",
    insight:
      "Don't automatically eliminate the meeting. First understand what function the meeting is compensating for.",
    format: "EXPLORE",
    href: "/patterns/meeting-should-be-email",
    symptom:
      "People spend time in recurring meetings just to exchange status updates or chase decisions.",
    underlying:
      "The organisation has no reliable workflow or information mechanism for the work happening between meetings.",
    lens: ["Information", "Decision", "Handoff", "Accountability", "Workflow"],
  },
  {
    slug: "process-nobody-owns",
    cats: ["process", "governance"],
    tag: "PROCESS · GOVERNANCE",
    title: "The Process Everyone Follows — and Nobody Owns",
    insight:
      "A process without ownership becomes everyone's responsibility — which often means nobody is actually accountable.",
    format: "EXPLORE",
    href: "/patterns/process-nobody-owns",
    symptom: "The process works most of the time, but nobody can clearly say who owns it.",
    underlying: "Execution exists without clear process ownership.",
    lens: ["Process", "Owner", "Decision Rights", "Exceptions", "Accountability"],
  },
  {
    slug: "one-person-knows",
    cats: ["people"],
    tag: "PEOPLE · KNOWLEDGE",
    title: "The Company Where Only One Person Knows How Things Work",
    insight:
      "If one person's absence can stop a process, you don't have resilience. You have a key-person dependency.",
    format: "EXPLORE",
    href: "/patterns/one-person-knows",
    symptom: "Everyone goes to the same person whenever something unusual happens.",
    underlying:
      "Critical organisational knowledge exists as individual knowledge rather than organisational infrastructure.",
    lens: ["Knowledge", "Dependency", "Documentation", "Access", "Resilience"],
  },
  {
    slug: "dont-automate-waiting",
    cats: ["ai"],
    tag: "AI & AUTOMATION",
    title: "Don't Automate the Waiting",
    insight:
      "Automating a slow approval process doesn't fix the decision that is causing the delay.",
    format: "WATCH",
    symptom: "Approvals take days, and the proposed fix is to automate the workflow.",
    underlying: "The delay sits in the decision, not in the mechanics of the process.",
    lens: ["Waiting", "Approval", "Ownership", "Decision", "Automation"],
  },
  {
    slug: "it-team-not-slow",
    cats: ["technology", "process"],
    tag: "TECHNOLOGY · PROCESS",
    title: "Your IT Team Isn't Slow",
    insight:
      "What looks like an IT capacity problem may actually be a broken request and decision process.",
    format: "WATCH",
    symptom: "Requests to IT take weeks, and the team is blamed for being slow.",
    underlying:
      "Requests arrive without clear requirements, ownership or a decision path — so they queue.",
    lens: ["Request", "Requirements", "Handoff", "Queue", "Fulfilment"],
  },
  {
    slug: "process-in-someones-head",
    cats: ["process"],
    tag: "PROCESS · KNOWLEDGE",
    title: "The Process That Exists Only in Someone's Head",
    insight:
      "If the process disappears when someone goes on holiday, it was never really a process.",
    format: "WATCH",
    symptom: "Work only moves correctly when a specific person is involved.",
    underlying: "The process has never been externalised into documentation or a system.",
    lens: ["Knowledge", "Dependency", "Documentation", "Standardisation", "Resilience"],
  },
  {
    slug: "bought-the-tool",
    cats: ["technology"],
    tag: "TECHNOLOGY · TRANSFORMATION",
    title: "We Bought the Tool. Why Isn't Anything Better?",
    insight: "Implementation is not transformation.",
    format: "WATCH",
    symptom: "A new system is live, but the problems it was bought to solve are still there.",
    underlying:
      "The tool was implemented around the existing process instead of the process being redesigned for the outcome.",
    lens: ["Problem", "Process", "Adoption", "Capability", "Technology"],
  },
  {
    slug: "everyone-responsible-nobody-accountable",
    cats: ["governance"],
    tag: "GOVERNANCE · OPERATING MODEL",
    title: "Everyone Is Responsible. Nobody Is Accountable.",
    insight:
      "Adding more people to a responsibility does not necessarily create accountability.",
    format: "WATCH",
    symptom: "When something goes wrong, everyone was involved — and no one was accountable.",
    underlying: "Responsibility is shared, but ownership and decision rights were never assigned.",
    lens: ["Responsibility", "Ownership", "Decision Rights", "Accountability", "Evidence"],
  },
];

export const CASES = [
  {
    slug: "employee-no-laptop",
    tag: "PEOPLE · PROCESS · IT",
    label: "Core · Real-world inspired / Anonymised",
    dim: "process",
    video: "Sf4HwcrBSo8",
    title: "The Employee Who Couldn't Get a Laptop",
    summary:
      "HR has completed onboarding. IT is waiting for the specification. The manager knows what the role requires, so the request waits for that information before IT can fulfil it.",
    diagnosis: {
      symptom: "IT appears slow because the employee is waiting for equipment.",
      finding:
        "The process depends on role-specific requirements known by the manager but not encoded in a reusable mechanism.",
      rootCause: "Missing decision mechanism / standardisation at the handoff between manager and IT.",
      recommendation:
        "Define standard role-based requirements for common roles; encode equipment, software and access; route genuine exceptions to the manager.",
    },
    questions: [
      "Where does onboarding stop and wait?",
      "What information is missing?",
      "Why does only one person have it?",
      "Could another competent person make the same decision?",
      "Would automation remove the dependency or just automate the waiting?",
    ],
  },
  {
    slug: "spreadsheet-company",
    tag: "PROCESS · INFORMATION",
    label: "Core · Editorial case concept",
    dim: "process",
    video: "1rWVOSiZM4I",
    title: "The Spreadsheet That Runs the Company",
    summary:
      "A critical spreadsheet has become the practical source of truth because formal systems, process ownership or information flows do not fully meet the operational need.",
    diagnosis: {
      symptom: "The spreadsheet looks like a technology problem.",
      finding: "It is compensating for a missing capability elsewhere in the operating model.",
      rootCause: "The underlying information, process or ownership need has not been resolved.",
      recommendation:
        "Map what the spreadsheet does; identify inputs, decisions, outputs and ownership; then decide whether to simplify, standardise, integrate or replace.",
    },
    questions: [
      "What decisions depend on it?",
      "Who owns the data?",
      "What happens if it is wrong or unavailable?",
      "Why did people start using it?",
      "What capability is missing from the formal process or system?",
    ],
  },
  {
    slug: "policy-nobody-reads",
    tag: "GOVERNANCE",
    label: "Core · Editorial case concept",
    dim: "governance",
    video: "szN7QImiQ2E",
    title: "The Policy Nobody Reads",
    summary:
      "A policy exists as documentation, but awareness, ownership, operational embedding and evidence of implementation are weak.",
    diagnosis: {
      symptom: "The organisation appears compliant because the policy exists.",
      finding: "Documentation is being mistaken for operational control.",
      rootCause: "The policy is disconnected from real work, ownership and evidence.",
      recommendation:
        "Connect policy requirements to real processes, owners, training and awareness, controls and evidence.",
    },
    questions: [
      "Who needs to act differently because of this policy?",
      "Where does it appear in a real workflow?",
      "Who owns implementation?",
      "What evidence would show it is actually followed?",
      "What happens when someone does not follow it?",
    ],
  },
  {
    slug: "meeting-should-be-email",
    tag: "PROCESS",
    label: "Core · Editorial case concept",
    dim: "process",
    title: "The Meeting That Should Have Been an Email",
    summary:
      "Recurring meetings are used to exchange status, chase actions or resolve information gaps that might otherwise be handled through a workflow or reliable source of truth.",
    diagnosis: {
      symptom: "The meeting looks like the problem.",
      finding:
        "The meeting may be compensating for missing visibility, decision rights, workflow or information flow.",
      rootCause: "There is no reliable mechanism for the function the meeting is performing.",
      recommendation:
        "Identify the meeting's actual purpose; separate information, decisions and escalation; design the simplest mechanism that performs each function.",
    },
    questions: [
      "What function does the meeting actually perform?",
      "What information is exchanged?",
      "Which decisions are made?",
      "Which actions are chased?",
      "What would need to exist for the meeting to become unnecessary?",
    ],
  },
  {
    slug: "process-nobody-owns",
    tag: "PROCESS · GOVERNANCE",
    label: "Core · Editorial case concept",
    dim: "governance",
    title: "The Process Everyone Follows — and Nobody Owns",
    summary:
      "A process operates through habit and cooperation but has unclear ownership, decision rights and accountability, especially when exceptions occur.",
    diagnosis: {
      symptom: "The process appears to work.",
      finding: "Execution exists without clear process ownership.",
      rootCause: "Ownership and decision rights were not explicitly designed.",
      recommendation:
        "Define process owner, decision rights, exception handling, measures and accountability.",
    },
    questions: [
      "Who owns the end-to-end outcome?",
      "Who can change the process?",
      "Who decides when an exception occurs?",
      "What happens when the process fails?",
      "What evidence shows whether it is working?",
    ],
  },
  {
    slug: "one-person-knows",
    tag: "PEOPLE · KNOWLEDGE",
    label: "Core · Editorial case concept",
    dim: "people",
    title: "The Company Where Only One Person Knows How Things Work",
    summary:
      "Critical operational knowledge is concentrated in an individual, making everyday work dependent on memory, experience and informal access.",
    diagnosis: {
      symptom: "The person is seen as invaluable.",
      finding:
        "The organisation has turned individual knowledge into infrastructure without designing for resilience.",
      rootCause:
        "Critical knowledge is not sufficiently documented, accessible, transferable or embedded in processes.",
      recommendation:
        "Map knowledge dependencies and identify what must be documented, standardised, trained or embedded.",
    },
    questions: [
      "Who is the person everyone goes to?",
      "What knowledge do they uniquely hold?",
      "Which processes depend on it?",
      "Could someone else perform the work using available information?",
      "What happens when they are unavailable?",
    ],
  },
];

export const SERVICES = [
  {
    id: "process-discovery",
    index: "01",
    title: "PROCESS DISCOVERY",
    text: "Understand how work actually happens, where it breaks and where unnecessary complexity exists.",
  },
  {
    id: "operating-model",
    index: "02",
    title: "OPERATING MODEL",
    text: "Identify unclear ownership, organisational dependencies and key-person risks.",
  },
  {
    id: "digital-transformation",
    index: "03",
    title: "DIGITAL TRANSFORMATION",
    text: "Define what should change before technology is introduced and where technology can create genuine value.",
  },
  {
    id: "governance",
    index: "04",
    title: "GOVERNANCE",
    text: "Translate policies, controls and regulatory requirements into operational practice.",
  },
  {
    id: "ai-automation",
    index: "05",
    title: "AI & AUTOMATION",
    text: "Identify where automation and AI can create value — and where automating the existing process would simply make the wrong process faster.",
  },
];

export const APPROACH_STEPS = [
  {
    n: "01",
    title: "DISCOVER",
    text: "Understand the current operating reality.",
  },
  {
    n: "02",
    title: "DIAGNOSE",
    text: "Find the root cause rather than treating the symptom.",
  },
  {
    n: "03",
    title: "DESIGN",
    text: "Define the future process, ownership and technology model.",
  },
  {
    n: "04",
    title: "TRANSFORM",
    text: "Turn the recommendation into an actionable roadmap.",
  },
];

export const METHODOLOGY = [
  {
    n: "01",
    title: "UNDERSTAND",
    text: "Start with how the organisation actually works, not how a process document says it works.",
  },
  {
    n: "02",
    title: "DIAGNOSE",
    text: "Identify what is working, what isn't, where risks and dependencies exist, and what should change first.",
  },
  {
    n: "03",
    title: "DESIGN",
    text: "Turn findings into a practical solution across processes, governance, systems, information, controls and workflows.",
  },
  {
    n: "04",
    title: "IMPLEMENT",
    text: "Work alongside the organisation to implement changes, establish processes and create evidence that they work.",
  },
  {
    n: "05",
    title: "IMPROVE",
    text: "Once the foundations are in place, identify what can be simplified, automated or improved next.",
  },
];

export const WORK_OUTLINES = [
  {
    title: "Care System Transformation",
    scope: "Care · Core Systems",
    href: "/work/care-system-transformation",
  },
  {
    title: "Microsoft 365 Transformation",
    scope: "Digital Workplace · Information Management",
    href: "/work/microsoft-365-transformation",
  },
  {
    title: "Governance / ISO 27001",
    scope: "Governance · Information Security",
    href: "/work/iso-27001-governance",
  },
];

export const CASE_STUDIES = [
  {
    slug: "care-system-transformation",
    tag: "CARE · CORE SYSTEMS",
    title: "Care System Transformation",
    subtitle: "Replacing a Core Care System Without Breaking the Ecosystem",
    sections: [
      {
        kicker: "Context",
        paragraphs: [
          "A public-sector organisation was preparing to replace its core social care system supporting multiple service areas.",
          "The challenge was not simply selecting and implementing a new platform.",
          "The new system needed to operate within a complex technology ecosystem, connecting with a range of existing systems supporting care delivery, workforce management, telecare, clinical information, scheduling and other organisational functions.",
        ],
      },
      {
        kicker: "The problem",
        paragraphs: [
          "The organisation relied on multiple systems, each supporting a different part of the operational landscape.",
          "The new core system therefore needed to:",
        ],
        bullets: [
          "support different service areas",
          "exchange information with existing platforms",
          "maintain data consistency",
          "work across different operational workflows",
          "support mobile and frontline users",
          "meet security and governance requirements",
          "remain adaptable as the wider technology landscape evolves",
        ],
        afterParagraphs: [
          "The challenge was to replace a core system without creating new gaps between the systems around it.",
        ],
      },
      {
        kicker: "What was actually happening",
        paragraphs: [
          "The transformation could not be approached as a standalone software implementation. The key questions were:",
        ],
        bullets: [
          "Where does critical information originate?",
          "Where does it need to go?",
          "Which system should be the source of truth?",
          "What happens when two systems need information from each other?",
          "What happens when an existing system cannot provide the required integration?",
        ],
      },
      {
        kicker: "My role",
        paragraphs: [
          "I worked across the business, technical and procurement aspects of the transformation, translating operational requirements into clear and testable requirements for the future system. This included:",
        ],
        bullets: [
          "mapping the wider systems landscape",
          "identifying integration and information dependencies",
          "defining functional and technical requirements",
          "assessing interoperability and API requirements",
          "identifying risks and gaps in the existing ecosystem",
          "considering data, identity and security requirements",
          "supporting supplier evaluation",
          "challenging assumptions around how the future system would operate within the wider environment",
        ],
      },
      {
        kicker: "Approach",
        steps: [
          {
            n: "01",
            title: "Understand the ecosystem",
            text: "Map the systems, users, processes and information flows surrounding the core platform.",
          },
          {
            n: "02",
            title: "Understand the information",
            text: "Identify what information moves between systems, who owns it and where it needs to be available.",
          },
          {
            n: "03",
            title: "Translate operations into requirements",
            text: "Turn business needs into specific, testable requirements rather than generic statements such as “the system should integrate.”",
          },
          {
            n: "04",
            title: "Test the exceptions",
            text: "The most important question is often not “Can these systems integrate?” but “What happens when they can't?”",
          },
          {
            n: "05",
            title: "Design for the wider operating environment",
            text: "Assess the new platform not only as a product, but as part of the organisation's future operating model.",
          },
        ],
      },
      {
        kicker: "Key insight",
        quote: "A core-system transformation is rarely just a system replacement.",
        quoteAccent:
          "The real challenge is redesigning the network of processes, information, people and technology around the system — without losing the ability to operate.",
      },
    ],
  },
  {
    slug: "microsoft-365-transformation",
    tag: "DIGITAL WORKPLACE · INFORMATION MANAGEMENT",
    title: "Microsoft 365 Transformation",
    subtitle: "From evaluating Google Workspace vs Microsoft 365 to building the new digital workplace",
    sections: [
      {
        kicker: "Context",
        paragraphs: [
          "The organisation was evaluating two possible directions for its digital workplace: Google Workspace and Microsoft 365.",
          "The decision was not simply about comparing feature lists. It required looking at the organisation's existing technology environment, ways of working, security, collaboration needs and future requirements.",
        ],
      },
      {
        kicker: "The challenge",
        paragraphs: ["The question was not: which platform has more features?"],
        standout: "Which ecosystem fits the organisation's operating model and future direction?",
        bulletsLabel: "The assessment considered areas such as:",
        bullets: [
          "collaboration and communication",
          "document and information management",
          "security and device management",
          "integration with the existing environment",
          "employee experience",
          "governance and administration",
          "future scalability",
        ],
      },
      {
        kicker: "The outcome",
        paragraphs: [
          "Following the assessment, the organisation selected Microsoft 365 as its strategic digital workplace platform.",
          "The transformation then moved from platform selection to implementation and adoption — translating the decision into a practical change in how people communicate, collaborate and manage information.",
        ],
      },
      {
        kicker: "Key insight",
        quote: "Technology selection is a transformation decision, not just a procurement decision.",
        quoteAccent:
          "The right question isn't “Which platform is better?” It's: “Which platform best supports how this organisation needs to operate?”",
      },
    ],
  },
  {
    slug: "iso-27001-governance",
    tag: "GOVERNANCE · INFORMATION SECURITY",
    title: "Governance / ISO 27001",
    subtitle: "Turning ISO 27001 From Documentation Into an Operating System",
    sections: [
      {
        kicker: "Context",
        paragraphs: [
          "A growing technology company decided to establish an ISO 27001-aligned Information Security Management System (ISMS). The initiative started from a leadership decision following discussions with external auditors.",
          "The organisation already had many of the practices needed to operate securely, but they were not structured as a formal management system. Important knowledge, responsibilities, processes and evidence were distributed across people and departments.",
        ],
      },
      {
        kicker: "The problem",
        paragraphs: [
          "The challenge was not simply to write policies or prepare a set of documents for an audit.",
          "The organisation needed to turn existing, largely informal ways of working into a coherent system with defined scope, risks, responsibilities, controls, policies and evidence.",
          "There was also a practical constraint: external candidates being considered to lead the initiative did not yet understand the company's products, processes and operating reality well enough to build the system effectively.",
        ],
      },
      {
        kicker: "What was actually happening",
        paragraphs: [
          "Security-related practices existed, but much of the organisational knowledge was implicit. People knew how things worked, but the organisation did not always have a reliable way to show that the relevant controls were defined, owned, performed and evidenced.",
          "This created a classic governance gap:",
        ],
        standout:
          "The organisation knew how it worked. The management system needed to know — and demonstrate — how it worked.",
      },
      {
        kicker: "My role",
        paragraphs: [
          "I proposed taking internal ownership of the implementation because I already understood the organisation's products, technology, teams and processes.",
          "At the start, I did not have formal ISO implementation experience. I learned the standard, completed relevant training and then applied the requirements directly to the organisation rather than trying to force the company into a generic template.",
          "The implementation covered:",
        ],
        bullets: [
          "ISMS scope and governance structure",
          "requirements and control mapping",
          "risk assessment and risk management",
          "Statement of Applicability",
          "a structured information-security policy framework, including 32 policies",
          "control ownership and operational evidence",
          "people-related security processes",
          "access and asset governance",
          "supplier and third-party governance",
          "security awareness and training",
          "incident management and related governance",
          "GDPR-related operational requirements and evidence",
        ],
      },
      {
        kicker: "The approach",
        steps: [
          {
            n: "01",
            title: "Understand the organisation",
            text: "Start with how the company actually operates: its products, people, systems, suppliers, information and dependencies.",
          },
          {
            n: "02",
            title: "Translate the standard into the organisation",
            text: "Map ISO requirements to real organisational activities rather than creating documentation in isolation.",
          },
          {
            n: "03",
            title: "Build ownership",
            text: "Identify who owns each policy, risk, control and recurring activity. A control without an owner is only a statement.",
          },
          {
            n: "04",
            title: "Build evidence as part of the process",
            text: "Define what demonstrates that a control is actually operating and where that evidence should exist.",
          },
          {
            n: "05",
            title: "Make governance operational",
            text: "Connect policies, risks, controls, people processes, suppliers and evidence so the ISMS becomes part of normal organisational management rather than an audit-time exercise.",
          },
        ],
      },
      {
        kicker: "The real challenge",
        paragraphs: [
          "One of the biggest lessons was that ISO implementation exposes organisational dependencies that may have existed unnoticed for years.",
          "When information is stored in people's heads, when ownership is unclear, or when a process exists only because someone remembers to do it, the problem is not simply documentation.",
          "It is an operating-model problem.",
        ],
      },
      {
        kicker: "Key insight",
        quote: "ISO 27001 implementation is not primarily a documentation project.",
        quoteAccent:
          "The real work is turning organisational knowledge into a system of ownership, risk management, controls and evidence that can survive beyond the people who currently know how everything works.",
      },
    ],
  },
];

export const LEARN_PRODUCTS = [
  {
    title: "Operating Reality Toolkit",
    text: "The frameworks behind the channel — process maps, dependency analysis and root-cause templates.",
    status: "IN DEVELOPMENT",
  },
  {
    title: "Digital Transformation Practitioner Toolkit",
    text: "Working tools for people who deliver transformation, not just talk about it.",
    status: "IN DEVELOPMENT",
  },
  {
    title: "Digital Transformation Course",
    text: "A structured course built on the same methodology: understand, diagnose, design, implement, improve.",
    status: "PLANNED",
  },
  {
    title: "Guides & Frameworks",
    text: "Short, practical guides that turn Operating Reality ideas into Monday-morning actions.",
    status: "IN DEVELOPMENT",
  },
];

export const EXPERIENCE_AREAS = [
  "Business Transformation",
  "Digital Transformation",
  "Product",
  "ISO 27001",
  "Governance",
  "Microsoft 365",
  "Information Management",
  "AI & Automation",
];

export const DIAGNOSTIC_QUESTIONS = [
  { dim: "process", text: "Work regularly stalls waiting for approvals, handoffs or decisions." },
  { dim: "people", text: "If one specific person is away, certain work simply stops." },
  { dim: "technology", text: "Teams rely on spreadsheets or email to fill the gaps between systems." },
  { dim: "governance", text: "Policies exist, but day-to-day practice has quietly drifted away from them." },
  { dim: "automation", text: "There is pressure to 'do something with AI' before agreeing what problem it should solve." },
  { dim: "process", text: "The same information is entered into more than one system or document." },
  { dim: "people", text: "New starters learn how things work by asking around, not from documentation." },
  { dim: "technology", text: "A tool was bought to fix a problem — and the problem is still there." },
  { dim: "governance", text: "When something goes wrong, it isn't obvious who is accountable." },
  { dim: "automation", text: "Processes are being considered for automation before they've been documented." },
  { dim: "process", text: "The process isn't slow — the decision is." },
  { dim: "people", text: "The same few people are consulted on almost every decision." },
  { dim: "technology", text: "Staff maintain their own workarounds alongside the official systems." },
  { dim: "governance", text: "Controls and compliance checks happen as an afterthought, not as part of the work." },
  { dim: "automation", text: "Manual, repetitive coordination consumes a significant share of the team's time." },
];

export const DIAGNOSTIC_DIMS = {
  process: {
    label: "Process",
    narrative:
      "Friction is concentrated in the flow of work itself — handoffs, approvals and re-entry of information. Map how work actually happens before buying anything.",
  },
  people: {
    label: "People & Knowledge",
    narrative:
      "Knowledge and decisions depend on individuals rather than systems. You may not have a process problem. You have a dependency.",
  },
  technology: {
    label: "Technology",
    narrative:
      "Technology is compensating for operational gaps. Another tool would hide the problem, not solve it.",
  },
  governance: {
    label: "Governance",
    narrative:
      "Practice has drifted from policy. Accountability needs to be designed into the work, not assumed from documents.",
  },
  automation: {
    label: "AI & Automation",
    narrative:
      "The pressure to automate is outpacing understanding. Fix the process first — automation makes the wrong process faster, not better.",
  },
};

export const DIAGNOSTIC_OPTIONS = [
  "Not true",
  "Rarely true",
  "Sometimes true",
  "Mostly true",
  "Exactly true",
];

export const DIAGNOSTIC_RESULT_RANGES = {
  process: [
    { min: 3, max: 5, label: "Low friction", summary: "Your processes flow reasonably well. Approvals and handoffs happen without regular delays. Keep mapping what actually happens versus what’s documented — drift accumulates quietly." },
    { min: 6, max: 8, label: "Moderate friction", summary: "There are noticeable delays in handoffs and approvals. Work stalls occasionally, but teams have adapted around it. Those adaptations are hiding the real cost. Worth mapping the critical path before it calcifies." },
    { min: 9, max: 11, label: "Significant friction", summary: "Processes are visibly slowing work. Duplicate data entry, unclear handoffs, and approval bottlenecks are part of the daily reality. This is the kind of friction people stop noticing because it’s always been there." },
    { min: 12, max: 15, label: "Critical friction", summary: "Process friction is a structural problem. Work stalls routinely waiting for decisions, information is re-entered across systems, and the actual workflow bears little resemblance to the documented one. This is where you start." },
  ],
  people: [
    { min: 3, max: 5, label: "Low dependency", summary: "Knowledge is reasonably distributed. Work continues when individuals are absent, and new starters can find what they need. Rare, and worth protecting." },
    { min: 6, max: 8, label: "Moderate dependency", summary: "A few key people hold disproportionate knowledge. The organisation works around them rather than through systems. Not yet painful, but a single departure would expose it." },
    { min: 9, max: 11, label: "Significant dependency", summary: "Critical decisions and knowledge sit with specific individuals. Onboarding is oral tradition. The bus factor is low and everyone knows it but nobody is addressing it." },
    { min: 12, max: 15, label: "Critical dependency", summary: "The organisation depends on individuals, not systems, for continuity. If one person leaves, entire workflows stop. This isn’t a knowledge management problem — it’s an operating model problem." },
  ],
  technology: [
    { min: 3, max: 5, label: "Low gap", summary: "Technology broadly supports how people actually work. Workarounds are minimal. This usually means someone made good decisions early, or the work is simple enough that the tools fit." },
    { min: 6, max: 8, label: "Moderate gap", summary: "There’s a growing distance between official systems and how work actually gets done. Shadow spreadsheets and workarounds are emerging. The tools aren’t broken — they just don’t match the work." },
    { min: 9, max: 11, label: "Significant gap", summary: "Staff maintain parallel systems alongside the official ones. Tools bought to solve problems haven’t — they’ve added complexity. The next purchase will do the same unless the underlying workflow is addressed." },
    { min: 12, max: 15, label: "Critical gap", summary: "Technology has become a layer of complexity rather than an enabler. Every system has a shadow process. Buying another tool will make this worse. Map the work, then decide what supports it." },
  ],
  governance: [
    { min: 3, max: 5, label: "Low drift", summary: "Policy and practice are broadly aligned. Accountability is clear, and controls are built into workflows. This is the foundation everything else rests on." },
    { min: 6, max: 8, label: "Moderate drift", summary: "Practice has started to drift from policy. Accountability is assumed rather than explicit. Compliance happens, but as a separate activity rather than part of the work." },
    { min: 9, max: 11, label: "Significant drift", summary: "There’s a visible gap between documented governance and daily reality. Accountability is unclear when things go wrong. Controls are afterthoughts. This creates risk that compounds over time." },
    { min: 12, max: 15, label: "Critical drift", summary: "Governance exists on paper but not in practice. Nobody is clearly accountable, and compliance is retrospective at best. This isn’t a documentation problem — accountability needs to be designed into the operating model." },
  ],
  automation: [
    { min: 3, max: 5, label: "Low pressure", summary: "Automation decisions are grounded in clear problem definitions. Manual work exists where it should. There’s no rush to automate for its own sake. This is healthy." },
    { min: 6, max: 8, label: "Moderate pressure", summary: "There’s growing pressure to automate, and not all of it is connected to specific problems. Some processes are being considered for automation before they’ve been properly understood. Worth slowing down." },
    { min: 9, max: 11, label: "Significant pressure", summary: "Automation is being pursued ahead of process understanding. Manual coordination consumes significant time, but the answer isn’t necessarily technology — it’s clarity." },
    { min: 12, max: 15, label: "Critical pressure", summary: "The pressure to automate has outpaced understanding of what the work actually is. Undocumented processes are being handed to AI. Manual coordination is the default. This is where automation becomes expensive and fragile." },
  ],
};

export const DIAGNOSTIC_OVERALL_RANGES = [
  { min: 15, max: 25, label: "Your organisation operates with relatively low friction." },
  { min: 26, max: 40, label: "There are real operational friction points worth examining." },
  { min: 41, max: 55, label: "Significant friction across multiple dimensions. This is where structured diagnosis pays off." },
  { min: 56, max: 75, label: "Operational friction is structural. This isn’t about quick fixes — the operating model needs attention." },
];

export const HIDDEN_SLUGS = [
  "dont-automate-waiting",
  "it-team-not-slow",
  "process-in-someones-head",
  "bought-the-tool",
  "everyone-responsible-nobody-accountable",
];
